import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { Button } from '../components/ui/Button'
import { fetchMfaStatus, type CurrentUser } from '../lib/api'

type Density = 'compact' | 'standard' | 'comfortable'
type Theme = 'light' | 'dark'

interface NavItem {
  to: string
  label: string
  /**
   * Hidden unless the user holds this permission. Omitted for pages that act
   * only on the caller's own account, which every user can always reach.
   */
  permission?: string
}

interface NavGroup {
  heading: string
  items: NavItem[]
}

const NAVIGATION: NavGroup[] = [
  {
    heading: 'People',
    items: [
      { to: '/hr/employees', label: 'Employees', permission: 'hr:employee:read' },
      { to: '/hr/leave', label: 'Leave', permission: 'hr:leave:read' },
      { to: '/hr/payroll', label: 'Payroll', permission: 'hr:payroll:read' },
    ],
  },
  {
    heading: 'Finance',
    items: [
      { to: '/finance/journal', label: 'Journal', permission: 'finance:journal:read' },
      { to: '/finance/invoices', label: 'Invoices', permission: 'finance:invoice:read' },
      { to: '/finance/reports', label: 'Reports', permission: 'finance:report:read' },
    ],
  },
  {
    heading: 'Supply chain',
    items: [
      { to: '/scm/products', label: 'Products', permission: 'scm:product:read' },
      { to: '/scm/stock', label: 'Stock', permission: 'scm:stock:read' },
      { to: '/scm/purchase-orders', label: 'Purchase orders', permission: 'scm:po:read' },
    ],
  },
  {
    heading: 'Customers',
    items: [
      { to: '/crm/accounts', label: 'Accounts', permission: 'crm:account:read' },
      { to: '/crm/pipeline', label: 'Pipeline', permission: 'crm:opportunity:read' },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/admin/users', label: 'Users & roles', permission: 'iam:user:read' },
      { to: '/admin/audit', label: 'Audit log', permission: 'iam:audit:read' },
    ],
  },
  {
    heading: 'Your account',
    items: [{ to: '/settings/security', label: 'Security' }],
  },
]

export interface AppShellProps {
  user: CurrentUser
  onSignOut: () => void
}

export function AppShell({ user, onSignOut }: AppShellProps) {
  // Both preferences are per-user and persisted locally. Density in
  // particular is a genuine accessibility and ergonomics setting, not a theme
  // gimmick: a controller reconciling 400 lines and an occasional approver
  // want measurably different row heights.
  const [density, setDensity] = useState<Density>(
    () => (localStorage.getItem('lg-density') as Density) ?? 'standard',
  )
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('lg-theme') as Theme | null
    if (stored) return stored
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  useEffect(() => {
    document.documentElement.dataset.density = density
    localStorage.setItem('lg-density', density)
  }, [density])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('lg-theme', theme)
  }, [theme])

  // After an administrator reset the account signs in on a password alone
  // until the grace window closes, and then simply stops working. Nothing
  // else in the product would ever mention that, so someone who does not
  // happen to open their security settings gets locked out with no warning.
  // One small request per app load is a fair price for not doing that to them.
  const [graceUntil, setGraceUntil] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchMfaStatus()
      .then((status) => {
        if (!cancelled) setGraceUntil(status.grace_until)
      })
      // A failure here must not break the shell. The security page reports
      // its own errors; a banner that could not load is simply absent.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  // A section is hidden entirely when the user holds none of its permissions,
  // rather than showing links that lead to a 403. This is a UX decision, not
  // a security one — every endpoint re-checks server-side regardless.
  const visibleGroups = NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.permission || item.permission in user.permissions),
  })).filter((group) => group.items.length > 0)

  return (
    <div className="lg-shell">
      {/* First tab stop on every page. Without it a keyboard user traverses
          the whole rail on every navigation. */}
      <a className="lg-skip-link" href="#main">
        Skip to main content
      </a>

      <nav className="lg-rail" aria-label="Main">
        <div className="lg-rail__brand">
          <span className="lg-rail__mark">Ledger</span>
          <span className="lg-overline">ERP</span>
        </div>

        {visibleGroups.map((group) => (
          <div className="lg-rail__group" key={group.heading}>
            <h2 className="lg-rail__heading">{group.heading}</h2>
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} className="lg-rail__link">
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="lg-main">
        <header className="lg-topbar">
          <span className="lg-overline">{user.email}</span>
          <span className="lg-spacer" />

          <label className="lg-row" style={{ gap: 'var(--lg-space-2)' }}>
            <span className="lg-overline">Density</span>
            <select
              className="lg-input"
              value={density}
              onChange={(e) => setDensity(e.target.value as Density)}
            >
              <option value="compact">Compact</option>
              <option value="standard">Standard</option>
              <option value="comfortable">Comfortable</option>
            </select>
          </label>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? 'Light' : 'Dark'}
          </Button>

          <Button variant="ghost" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </header>

        <GraceBanner until={graceUntil} />

        {/* tabIndex={-1} so the skip link can move focus here. Without it the
            browser scrolls but focus stays in the rail, and the next Tab
            resumes from the navigation. */}
        <main className="lg-content" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * The standing reminder that a reset account has a deadline.
 *
 * Suppressed on the security page itself, which states the same thing with
 * the actual controls next to it — repeating a warning directly above the
 * screen that resolves it is how banners get tuned out everywhere else.
 */
function GraceBanner({ until }: { until: string | null }) {
  const { pathname } = useLocation()
  if (!until || pathname === '/settings/security') return null

  return (
    <div className="lg-banner" role="status">
      <span>
        Two-factor authentication is off on your account until{' '}
        {new Date(until).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}.
        After that you will not be able to sign in without it.
      </span>
      <Link to="/settings/security">Set it up</Link>
    </div>
  )
}
